import React from 'react';
import { HelpCircle, ExternalLink, FileText, WandSparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RelatedQuestionItemProps } from '@/hooks/research-generator/related-questions/types';
import { isGeneratedQuestion, getQuestionText, getResearchId, truncateQuestion, extractCleanQuestionText } from '@/hooks/research-generator/related-questions/utils';

export const RelatedQuestionItem: React.FC<RelatedQuestionItemProps> = ({
  question,
  index,
  isGenerating,
  onGenerate,
  onNavigate
}) => {
  const isGenerated = isGeneratedQuestion(question);
  const questionText = getQuestionText(question);
  const researchId = getResearchId(question);
  
  // Final processing layer: Clean text for Generated Research components
  const cleanText = isGenerated ? extractCleanQuestionText(questionText) : questionText;
  const truncatedText = truncateQuestion(cleanText);

  const handleClick = async () => {
    if (isGenerating) return;

    if (isGenerated && researchId) {
      onNavigate(researchId);
    } else {
      await onGenerate(questionText, index);
    }
  };

  // Style classes based on question state
  const baseClasses = "w-full p-3 md:p-3 rounded-2xl border transition-all duration-200 text-left group";
  
  const stateClasses = isGenerated
    ? "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-800 hover:from-purple-100 hover:to-indigo-100 dark:hover:from-purple-900/30 dark:hover:to-indigo-900/30"
    : "bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800 hover:from-orange-100 hover:to-amber-100 dark:hover:from-orange-900/30 dark:hover:to-amber-900/30";

  const IconComponent = isGenerated ? FileText : HelpCircle;
  const iconColor = isGenerated ? "text-purple-500" : "text-orange-500";
  const textColor = isGenerated 
    ? "text-purple-700 dark:text-purple-300" 
    : "text-orange-700 dark:text-orange-300";
  const labelColor = isGenerated 
    ? "text-purple-600 dark:text-purple-400" 
    : "text-orange-600 dark:text-orange-400";

  // Different top right icon based on state
  const TopRightIcon = isGenerated ? ExternalLink : WandSparkles;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleClick}
          disabled={isGenerating}
          className={`${baseClasses} ${stateClasses} ${isGenerating ? 'cursor-not-allowed' : ''}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {isGenerating ? (
                  <div className={`animate-spin rounded-full h-3 w-3 border-2 ${
                    isGenerated 
                      ? 'border-purple-300 border-t-purple-600' 
                      : 'border-orange-300 border-t-orange-600'
                  } flex-shrink-0`} />
                ) : (
                  <IconComponent size={12} className={`${iconColor} flex-shrink-0`} />
                )}
                <span className={`text-xs font-medium ${labelColor}`}>
                  {isGenerated ? 'Generated Research' : isGenerating ? 'Generating...' : 'Related Question'}
                </span>
              </div>
              <p className={`text-sm ${textColor} leading-relaxed`}>
                {truncatedText}
              </p>
            </div>
            <TopRightIcon 
              size={12} 
              className={`${iconColor} opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1`} 
            />
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side="left" 
        className="max-w-xs p-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg"
      >
        <p className="text-gray-900 dark:text-gray-100 leading-relaxed">
          {cleanText}
        </p>
        {isGenerated && (
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            Click to view generated research
          </p>
        )}
        {!isGenerated && (
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            Click to generate research from this question
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}; 